import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface BusinessTranslation {
  id?: number;
  business_listing_id?: number;
  locale: string;
  name: string;
  description?: string;
}

export interface Business {
  id: string;
  name?: string;
  description?: string;
  category?: string;
  image?: string;
  gallery?: string[];
  owner?: string;
  established?: string;
  location?: string;
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  services?: string[];
  features?: string[];
  hours?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  };
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  rating?: number;
  reviews?: number;
  featured?: boolean;
  verified?: boolean;
  translations?: BusinessTranslation[];
  created_at?: string;
  updated_at?: string;
}

export interface BusinessesResponse {
  businesses: Business[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  message?: string;
  status?: string;
}

export interface BusinessResponse {
  business: Business;
  message?: string;
  status?: string;
}

export interface BusinessesQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  category?: string;
  verified?: boolean;
  featured?: boolean;
  admin?: string;
}

// Query keys
export const BUSINESSES_QUERY_KEY = ["businesses"];
export const BUSINESS_QUERY_KEY = (id: number) => ["businesses", id];

// Fetch businesses with pagination
const fetchBusinesses = async (
  params: BusinessesQueryParams = {}
): Promise<BusinessesResponse> => {
  // Convert boolean values to "1" or "0" strings for Laravel validation
  const queryParams: Record<string, unknown> = { ...params };
  if (queryParams.verified !== undefined) {
    queryParams.verified = queryParams.verified ? "1" : "0";
  }
  if (queryParams.featured !== undefined) {
    queryParams.featured = queryParams.featured ? "1" : "0";
  }

  const response = await publicRequest.get("/businesses", {
    params: queryParams,
  });
  // Response structure: { success: true, data: { businesses: [...], pagination: {...} }, message: "..." }
  return response.data.data;
};

// Fetch single business
const fetchBusiness = async (id: number): Promise<Business> => {
  const response = await publicRequest.get(`/businesses/${id}`);
  // Response structure: { success: true, data: { business: {...} }, message: "..." }
  return response.data.data.business || response.data.data;
};

// Create business (admin only)
const createBusiness = async (businessData: {
  category: string;
  image?: string;
  gallery?: string[];
  owner: string;
  established?: string;
  location: string;
  contact_phone: string;
  contact_email: string;
  contact_website?: string;
  services?: string[];
  features?: string[];
  hours?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  };
  social_media?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  rating?: number;
  reviews?: number;
  featured?: boolean;
  verified?: boolean;
  translations: BusinessTranslation[];
}): Promise<Business> => {
  const response = await privateRequest.post("/businesses", businessData);
  // Response structure: { success: true, data: { business: {...} }, message: "..." }
  return response.data.data.business || response.data.data;
};

// Update business (admin only)
const updateBusiness = async ({
  id,
  businessData,
}: {
  id: number;
  businessData: {
    category?: string;
    image?: string;
    gallery?: string[];
    owner?: string;
    established?: string;
    location?: string;
    contact_phone?: string;
    contact_email?: string;
    contact_website?: string;
    services?: string[];
    features?: string[];
    hours?: {
      monday?: string;
      tuesday?: string;
      wednesday?: string;
      thursday?: string;
      friday?: string;
      saturday?: string;
      sunday?: string;
    };
    social_media?: {
      facebook?: string;
      instagram?: string;
      twitter?: string;
    };
    rating?: number;
    reviews?: number;
    featured?: boolean;
    verified?: boolean;
    translations?: BusinessTranslation[];
  };
}): Promise<Business> => {
  const response = await privateRequest.put(`/businesses/${id}`, businessData);
  // Response structure: { success: true, data: { business: {...} }, message: "..." }
  return response.data.data.business || response.data.data;
};

// Delete business (admin only)
const deleteBusiness = async (id: number): Promise<void> => {
  await privateRequest.delete(`/businesses/${id}`);
};

// Custom hooks
export const useBusinesses = (params: BusinessesQueryParams = {}) => {
  const result = useQuery({
    queryKey: [...BUSINESSES_QUERY_KEY, params],
    queryFn: () => fetchBusinesses(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return result;
};

// Admin hook that includes translations
export const useBusinessesAdmin = (params: BusinessesQueryParams = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  return useQuery({
    queryKey: [
      ...BUSINESSES_QUERY_KEY,
      "admin",
      { ...params, locale: currentLanguage },
    ],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        per_page: (params.per_page || 15).toString(),
        page: (params.page || 1).toString(),
        locale: params.locale || currentLanguage,
        admin: "true",
        ...(params.search && { search: params.search }),
        ...(params.category && { category: params.category }),
        ...(params.verified !== undefined && {
          verified: params.verified ? "1" : "0",
        }),
        ...(params.featured !== undefined && {
          featured: params.featured ? "1" : "0",
        }),
      });

      const response = await privateRequest.get(`/businesses?${queryParams}`);
      // Return full response to match Events pattern
      return response.data;
    },
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useBusiness = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: BUSINESS_QUERY_KEY(id),
    queryFn: () => fetchBusiness(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useBusinessAdmin = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...BUSINESS_QUERY_KEY(id), "admin"],
    queryFn: async () => {
      const response = await privateRequest.get(`/businesses/${id}?admin=true`);
      // Response structure: { success: true, data: { business: {...} }, message: "..." }
      return response.data.data.business || response.data.data;
    },
    enabled,
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBusiness,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_KEY });
    },
  });
};

export const useUpdateBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusiness,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_KEY });
    },
  });
};

export const useDeleteBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBusiness,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_KEY });
    },
  });
};
